import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { LocalNotifications } from "@capacitor/local-notifications";
import axios from "axios";
import i18n from '../i18n';
import { deviceTimezone } from '../lib/deviceTimezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pick a short, brand-aligned notification phrase based on hour and user premium status
const pickPhraseForTime = (hour, isPremium = false) => {
  const langIsIt = i18n.language?.startsWith('it');
  const ns = 'notifications.phrases';
  const pickArray = (key) => i18n.t(`${ns}.${key}`, { returnObjects: true }) || [];

  // choose moment
  const momentKey = hour < 12 ? 'morning' : (hour >= 18 ? 'evening' : 'neutral');
  let pool = pickArray(momentKey);

  // occasionally prefer identity/premium/minimal variations
  if (isPremium && Math.random() < 0.25) {
    pool = pool.concat(pickArray('premium'));
  } else if (Math.random() < 0.08) {
    pool = pool.concat(pickArray('identity'));
  } else if (Math.random() < 0.06) {
    pool = pool.concat(pickArray('minimal'));
  }

  if (!pool || pool.length === 0) {
    pool = pickArray('neutral');
  }

  return pool[Math.floor(Math.random() * pool.length)] || (langIsIt ? 'Per oggi' : i18n.language?.startsWith('es') ? 'Para hoy' : "Today's reminder");
};
export const useCapacitorNotifications = (userId) => {
  const [isNative, setIsNative] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState("prompt");
  const [pushToken, setPushToken] = useState(null);

  // Determina se siamo su device nativo
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    setIsNative(platform === "ios" || platform === "android");
  }, []);

  // Inizializza push notifications solo su device nativo
  const initializePush = useCallback(async () => {
    if (!isNative) return;

    try {
      // Controlla i permessi
      let permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === "prompt") {
        permStatus = await PushNotifications.requestPermissions();
      }
      setPermissionStatus(permStatus.receive);

      if (permStatus.receive !== "granted") {
        console.log("Push notifications permission not granted");
        return;
      }

      await PushNotifications.register();

      // Listener registrazione token
      const registrationListener = PushNotifications.addListener(
        "registration",
        async (token) => {
          const tokenValue = token?.value;
          console.log("Push registration success, token:", tokenValue);
          setPushToken(tokenValue);

          if (userId && tokenValue) {
            try {
              await axios.post(`${API}/push/register-device`, {
                user_id: userId,
                token: tokenValue,
                platform: Capacitor.getPlatform(),
                // Tells the server this version syncs the zone itself (UserContext),
                // so it doesn't guess one from the connection.
                timezone: deviceTimezone(),
              });
            } catch (err) {
              console.error("Error registering device token:", err);
            }
          }
        }
      );

      // Listener errori registrazione
      const errorListener = PushNotifications.addListener(
        "registrationError",
        (err) => console.error("Push registration failed:", err)
      );

      // Listener notifica ricevuta in foreground
      const receivedListener = PushNotifications.addListener(
        "pushNotificationReceived",
        (notification) => {
          console.log("Push notification received:", notification);
        }
      );

      // Listener tap sulla notifica
      const actionListener = PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (event) => {
          // Protezione completa: Capacitor 5+ e fallback
          const data = event?.notification?.data || event?.data;
          if (!data) return;

          if (data.url) {
            window.location.href = data.url;
          }
        }
      );

      // Cleanup listener quando l'hook viene smontato
      return () => {
        registrationListener.remove();
        errorListener.remove();
        receivedListener.remove();
        actionListener.remove();
      };
    } catch (err) {
      console.error("Error initializing push notifications:", err);
    }
  }, [isNative, userId]);

  // Schedule di una notifica locale sicura
  const scheduleLocalNotification = useCallback(
    async (options) => {
      if (!isNative) return;
      try {
        const perm = await LocalNotifications.checkPermissions();
        if (perm.display !== "granted") {
          await LocalNotifications.requestPermissions();
        }

        await LocalNotifications.schedule({
          notifications: [
            {
              id: options.id || Date.now(),
              title: options.title,
              body: options.body,
              schedule: options.schedule || { at: new Date(Date.now() + 1000) },
              sound: "default",
              extra: options.data || {},
            },
          ],
        });
      } catch (err) {
        console.error("Error scheduling local notification:", err);
      }
    },
    [isNative]
  );

  // Reminder giornaliero
  const scheduleDailyReminder = useCallback(
    async (hour, minute) => {
      if (!isNative) return;
      try {
        // Cancel only the daily reminder notification (id 1001)
        try { await LocalNotifications.cancel({ notifications: [{ id: 1001 }] }); } catch (_) {}

        // try to detect premium user (best-effort) so premium phrasing can be used occasionally
        let isPremiumUser = false;
        try {
          if (userId) {
            const userRes = await axios.get(`${API}/user/${userId}`);
            const profile = userRes.data || {};
            isPremiumUser = profile.subscription_status === 'premium' || profile.role === 'betatester';
          }
        } catch (err) {
          // ignore - fallback to non-premium phrasing
          console.debug('Could not fetch user for notification phrasing:', err?.message || err);
        }

        const bodyText = pickPhraseForTime(hour, isPremiumUser);

        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture('notification_phrase_shown', {
            source: 'native_daily_reminder',
            phrase: bodyText,
            lang: i18n.language || null,
            user_id: userId || null,
            scheduled_for: `${hour}:${String(minute).padStart(2, '0')}`
          });
        }

        await LocalNotifications.schedule({
          notifications: [
            {
              id: 1001,
              title: i18n.language?.startsWith('it') ? 'Better Husband' : i18n.language?.startsWith('es') ? 'Better Husband' : 'Better Husband',
              body: bodyText,
              schedule: {
                on: { hour, minute },
                repeats: true,
                allowWhileIdle: true,
              },
              sound: 'default',
            },
          ],
        });
        console.log(`Daily reminder scheduled for ${hour}:${minute} — phrase: ${bodyText}`);
      } catch (err) {
        console.error("Error scheduling daily reminder:", err);
      }
    },
    [isNative, userId]
  );

  // Check-in serale
  const scheduleEveningCheckin = useCallback(async (hour = 22, minute = 0) => {
    if (!isNative) return;
    try {
      // Cancel only the evening check-in notification (id 1002)
      try { await LocalNotifications.cancel({ notifications: [{ id: 1002 }] }); } catch (_) {}

      // evening phrasing — prefer evening pool but occasionally use identity/minimal
      const bodyTextEvening = pickPhraseForTime(hour, false);

      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('notification_phrase_shown', {
          source: 'native_evening_checkin',
          phrase: bodyTextEvening,
          lang: i18n.language || null,
          user_id: userId || null,
          scheduled_for: `${hour}:${String(minute).padStart(2, '0')}`
        });
      }

      await LocalNotifications.schedule({
        notifications: [
          {
            id: 1002,
            title: i18n.language?.startsWith('it') ? 'Check-in serale 🌙' : i18n.language?.startsWith('es') ? 'Check-in nocturno 🌙' : 'Evening check-in 🌙',
            body: bodyTextEvening,
            schedule: {
              on: { hour, minute },
              repeats: true,
              allowWhileIdle: true,
            },
            sound: 'default',
          },
        ],
      });
      console.log(`Evening check-in scheduled for ${hour}:${minute.toString().padStart(2, '0')} — phrase: ${bodyTextEvening}`);
    } catch (err) {
      console.error("Error scheduling evening check-in:", err);
    }
  }, [isNative, userId]);

  // Cleanup di tutte le notifiche
  const cancelAllNotifications = useCallback(async () => {
    if (!isNative) return;
    try {
      await LocalNotifications.cancelAll();
      console.log("All notifications cancelled");
    } catch (err) {
      console.error("Error cancelling notifications:", err);
    }
  }, [isNative]);
  useEffect(() => {
    if (isNative && userId) {
      let cleanupFn;
      initializePush().then(fn => { cleanupFn = fn; });
      return () => { if (typeof cleanupFn === 'function') cleanupFn(); };
    }
  }, [isNative, userId, initializePush]);

  // NOTE: Do NOT cancel scheduled notifications on unmount — they must persist
  // across component re-renders and route changes. Only cancel on explicit logout
  // (handled in CapacitorInit.js when userId becomes null).

  return {
    isNative,
    permissionStatus,
    pushToken,
    initializePush,
    scheduleLocalNotification,
    scheduleDailyReminder,
    scheduleEveningCheckin,
    cancelAllNotifications,
  };
};

export default useCapacitorNotifications;
